import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Play } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { usePlayer } from '../context/PlayerContext';
import { motion } from 'framer-motion';
import { API } from '../config/api';
import { getProfileUrl } from '../utils/profileUtils';



export default function TracksPage() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playTrack } = usePlayer();

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const response = await fetch(`${API}/tracks?limit=50`);
      if (response.ok) setTracks(await response.json());
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="tracks-page">
      <div className="max-w-[1800px] mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">Tracks</h1>
          <p className="text-muted-foreground">Browse all music from DMV artists</p>
        </div>

        {tracks.length > 0 ? (
          <div className="space-y-2">
            {tracks.map((track, index) => (
              <motion.div
                key={track.track_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
                className="track-row flex items-center gap-4 p-3 rounded-lg cursor-pointer group"
                onClick={() => playTrack(track, tracks)}
              >
                <span className="w-6 text-center text-sm text-muted-foreground">{index + 1}</span>
                <div className="w-12 h-12 bg-muted rounded overflow-hidden relative flex-shrink-0">
                  {track.cover_url ? (
                    <img src={track.cover_url} alt={track.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                      <Music className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="play-icon absolute inset-0 bg-background/50 flex items-center justify-center">
                    <Play className="w-5 h-5 text-white" fill="white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{track.title}</p>
                  <Link 
                    to={track.artist_handle ? getProfileUrl({ handle: track.artist_handle }) : `/artist/${track.artist_id}`}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    {track.artist_name}
                  </Link>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
                  {track.genre && <Badge variant="outline" className="text-xs">{track.genre}</Badge>}
                  <span>{track.plays?.toLocaleString() || 0} plays</span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-muted-foreground">
            <Music className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="font-heading text-2xl font-bold mb-2">No Tracks Yet</h3>
            <p>Be the first artist to upload music!</p>
          </div>
        )}
      </div>
    </div>
  );
}
